<script setup lang="ts">
import { ref } from "vue";
import { Settings, LogOut, House, Users, BookOpen, ExternalLink } from "@lucide/vue";
import i18n from "@/services/i18n";
import Divider from "@/components/Reusables/Divider/Divider.vue";
import SpeckleLogo from "@/assets/Logos/speckle.svg";
import { openSpeckleModal } from "@features/Speckle";
import { useAdminPortal } from "@/services/fronteggTheme";
import { useToast } from "vue-toastification";
import router from "../../router/router";
import { useAuthStateStore } from "../../stores/Utils/auth/AuthState";

import { clearLocalStorage } from "../../services/Utils/clearLocalStorage";
import { storeToRefs } from "pinia";
import { useFlowGuideCoordinationStore } from "../../stores/flowGuide/flowGuideCoordination.store";

const adminPortal = useAdminPortal();
const authState = useAuthStateStore();
const { isAuthenticated, user } = storeToRefs(authState);

const logout = async () => {
  emits("hideProfileCard");

  clearLocalStorage();

  router.push("/account/logout");
  useToast().info("Successfully logged out");
};

const emits = defineEmits(["hideProfileCard", "toggleLCAda"]);

const showAdminPortal = () => {
  // AdminPortal is a Frontegg modal that renders on top with no way to signal v-onboarding it has
  // opened, so an active onboarding tour (whose "Profile" step spotlights the button that opens
  // this menu) stays mounted underneath, crowded into a barely-usable sliver. Close it first.
  useFlowGuideCoordinationStore().closeActiveTour();
  adminPortal.show();
  emits("hideProfileCard");
};

type LocaleCode = "en" | "da" | "sv" | "de" | "is" | "no";

const availableLanguages: { code: LocaleCode; label: string }[] = [
  { code: "en", label: "English" },
  { code: "da", label: "Danish" },
  { code: "sv", label: "Swedish" },
  { code: "de", label: "German" },
  { code: "is", label: "Icelandic" },
  { code: "no", label: "Norwegian" },
];

const selectedLanguage = ref<LocaleCode>((localStorage.getItem("locale") as LocaleCode) || i18n.global.locale.value);

const changeLanguage = (newLanguage: LocaleCode) => {
  i18n.global.locale.value = newLanguage;
  selectedLanguage.value = newLanguage;
  localStorage.setItem("locale", newLanguage);
  emits("hideProfileCard");
};

const showSpeckleModal = () => {
  emits("hideProfileCard");
  openSpeckleModal();
};

const externalLinks = [
  { label: "Homepage", url: "https://realtimelca.com", icon: House },
  { label: "Community", url: "https://community.realtimelca.com", icon: Users },
  { label: "Docs", url: "https://docs.realtimelca.com", icon: BookOpen },
];
</script>

<template>
  <div class="flex w-64 flex-col p-1">
    <div class="gap-px-12 flex items-center px-3 py-2">
      <img
        v-if="isAuthenticated"
        :src="user?.profilePictureUrl || undefined"
        class="h-9 w-9 shrink-0 rounded-full object-cover"
        alt="profile-picture" />
      <div class="flex min-w-0 flex-col">
        <span class="t-small-bold text-type-default truncate">{{ user?.name }}</span>
        <span class="t-xs text-type-muted truncate">{{ user?.email }}</span>
      </div>
    </div>

    <Divider spacing="sm" />

    <button
      type="button"
      data-cy="profile-btn"
      class="focus-ring interactive-active gap-px-8 text-type-default duration-fast hover:bg-app-blue-200/30 ease-standard flex w-full items-center rounded-md px-3 py-2 text-left transition-colors"
      @click="showAdminPortal">
      <Settings
        :stroke-width="1.5"
        class="icon-sm shrink-0"
        aria-hidden="true" />
      <span class="t-small flex-1 text-inherit">Profile</span>
    </button>

    <Divider spacing="sm" />

    <!-- External resources: real anchors (not window.open) so cmd/middle-click and copy-link
         work. Closing the popover on click keeps it from lingering behind the new tab. -->
    <a
      v-for="link in externalLinks"
      :key="link.url"
      :href="link.url"
      target="_blank"
      rel="noopener noreferrer"
      class="focus-ring interactive-active gap-px-8 text-type-default duration-fast hover:bg-app-blue-200/30 ease-standard flex w-full items-center rounded-md px-3 py-2 text-left transition-colors"
      @click="emits('hideProfileCard')">
      <component
        :is="link.icon"
        :stroke-width="1.5"
        class="icon-sm shrink-0"
        aria-hidden="true" />
      <span class="t-small flex-1 text-inherit">{{ link.label }}</span>
      <ExternalLink
        :stroke-width="1.5"
        class="icon-xs text-type-muted shrink-0"
        aria-hidden="true" />
    </a>
    <Divider spacing="sm" />
    <p class="t-caption-bold text-slate-450 pt-px-8 pb-px-6 px-4 tracking-wide uppercase">Integrations</p>

    <button
      type="button"
      class="focus-ring interactive-active gap-px-8 text-type-default duration-fast hover:bg-app-blue-200/30 ease-standard flex w-full items-center rounded-md px-3 py-2 text-left transition-colors"
      @click="showSpeckleModal">
      <img
        :src="SpeckleLogo"
        class="icon-sm shrink-0"
        alt=""
        aria-hidden="true" />
      <span class="t-small flex-1 text-inherit">Speckle</span>
    </button>

    <Divider spacing="sm" />

    <p class="t-caption-bold text-slate-450 pt-px-8 pb-px-6 px-4 tracking-wide uppercase">Category Language</p>

    <div class="pb-px-8 px-3">
      <select
        v-model="selectedLanguage"
        aria-label="Category language"
        class="focus-input rounded-8 bg-slate-0 t-small w-full border border-slate-200 px-3 py-2"
        @change="changeLanguage(selectedLanguage)">
        <option
          v-for="lang in availableLanguages"
          :key="lang.code"
          :value="lang.code">
          {{ lang.label }}
        </option>
      </select>
    </div>

    <Divider spacing="sm" />

    <button
      type="button"
      class="focus-ring interactive-active gap-px-8 text-type-default duration-fast hover:bg-app-blue-200/30 ease-standard flex w-full items-center rounded-md px-3 py-2 text-left transition-colors"
      @click="logout">
      <LogOut
        :stroke-width="1.5"
        class="icon-sm shrink-0"
        aria-hidden="true" />
      <span class="t-small flex-1 text-inherit">Log out</span>
    </button>
  </div>
</template>
